// Rich-text editor for email template bodies.
//
// Deliberately not a general-purpose WYSIWYG. Email HTML only reliably supports
// a small set of tags with inline styles — no flexbox, no stylesheets, and
// Outlook ignores much of what a browser renders happily. A general editor
// emits markup that looks right here and breaks in an inbox, so this one is
// constrained to tags the renderer is known to handle, and pasted content is
// stripped to plain text rather than carrying Word/Docs markup in with it.
import React, { useRef, useEffect, useCallback } from 'react';
import { Box, IconButton, Tooltip, Divider, Menu, MenuItem, Button } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import LinkIcon from '@mui/icons-material/Link';
import TitleIcon from '@mui/icons-material/Title';
import CodeIcon from '@mui/icons-material/Code';
import LinkOffIcon from '@mui/icons-material/LinkOff';

// Tags the email renderer is known to handle. Anything else is unwrapped,
// keeping its text.
const ALLOWED = new Set(['P', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'A', 'UL', 'OL', 'LI', 'H3', 'SPAN']);

/** Strip everything the email shell can't render, in place. */
function sanitizeTree(root) {
  for (const el of [...root.querySelectorAll('*')]) {
    if (!ALLOWED.has(el.tagName)) {
      el.replaceWith(...el.childNodes);
      continue;
    }
    for (const attr of [...el.attributes]) {
      const keep = (el.tagName === 'A' && (attr.name === 'href' || attr.name === 'target'))
        || attr.name === 'style';
      if (!keep) el.removeAttribute(attr.name);
    }
    // Only inline colour/weight survive; anything layout-related is dropped
    // because it behaves unpredictably across email clients.
    if (el.hasAttribute('style')) {
      const safe = el.getAttribute('style')
        .split(';')
        .map(s => s.trim())
        .filter(s => /^(color|font-weight|font-style|text-decoration)\s*:/i.test(s))
        .join('; ');
      if (safe) el.setAttribute('style', safe); else el.removeAttribute('style');
    }
  }
}

export default function EmailBodyEditor({ value, onChange, variables = [], minHeight = 260 }) {
  const ref = useRef(null);
  const [varAnchor, setVarAnchor] = React.useState(null);
  const savedRange = useRef(null);

  // Only write into the DOM when the incoming value genuinely differs, or the
  // caret jumps to the start on every keystroke.
  useEffect(() => {
    if (ref.current && value !== ref.current.innerHTML) {
      ref.current.innerHTML = value || '';
    }
  }, [value]);

  const emit = useCallback(() => {
    if (!ref.current) return;
    sanitizeTree(ref.current);
    onChange(ref.current.innerHTML);
  }, [onChange]);

  const exec = (cmd, arg) => {
    ref.current?.focus();
    // execCommand is deprecated but is the only thing every browser still
    // implements consistently for contentEditable, and the output is the
    // simple markup email wants.
    document.execCommand(cmd, false, arg);
    emit();
  };

  const rememberCaret = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && ref.current?.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const insertAtCaret = (text) => {
    ref.current?.focus();
    const sel = window.getSelection();
    if (savedRange.current) {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
    document.execCommand('insertText', false, text);
    emit();
  };

  /** The <a> the caret currently sits inside, if any. */
  const linkAtCaret = () => {
    const sel = window.getSelection();
    let n = sel && sel.anchorNode;
    while (n && n !== ref.current) {
      if (n.tagName === 'A') return n;
      n = n.parentNode;
    }
    return null;
  };

  const restoreCaret = () => {
    ref.current?.focus();
    if (savedRange.current) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
  };

  // Doubles as edit: with the caret inside a link, the prompt is pre-filled
  // with its current URL and the whole link is selected, so entering a new one
  // replaces it. Clearing the field removes the link but keeps the text.
  const addLink = () => {
    const existing = linkAtCaret();
    const url = window.prompt(
      existing
        ? 'Edit link URL — clear the field to remove the link:'
        : 'Link URL — a variable like [[buySessionsUrl]] works too:',
      existing ? existing.getAttribute('href') || '' : '',
    );
    if (url === null) return;            // cancelled
    if (existing) {
      // Select the entire link so createLink/unlink apply to all of it, not
      // just the caret position.
      const r = document.createRange();
      r.selectNodeContents(existing);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(r);
      ref.current?.focus();
      document.execCommand(url.trim() ? 'createLink' : 'unlink', false, url.trim() || undefined);
    } else {
      if (!url.trim()) return;
      restoreCaret();
      document.execCommand('createLink', false, url.trim());
    }
    emit();
  };

  const removeLink = () => {
    const existing = linkAtCaret();
    if (existing) {
      const r = document.createRange();
      r.selectNodeContents(existing);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(r);
      ref.current?.focus();
    } else {
      restoreCaret();
    }
    document.execCommand('unlink');
    emit();
  };

  const btn = (title, icon, onClick) => (
    <Tooltip title={title}>
      <IconButton size="small" onMouseDown={e => { e.preventDefault(); rememberCaret(); }} onClick={onClick}>
        {icon}
      </IconButton>
    </Tooltip>
  );

  return (
    <Box sx={{ border: '1px solid rgba(0,0,0,0.23)', borderRadius: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, p: 0.5, borderBottom: '1px solid rgba(0,0,0,0.12)', flexWrap: 'wrap' }}>
        {btn('Bold', <FormatBoldIcon fontSize="small" />, () => exec('bold'))}
        {btn('Italic', <FormatItalicIcon fontSize="small" />, () => exec('italic'))}
        {btn('Heading', <TitleIcon fontSize="small" />, () => exec('formatBlock', '<h3>'))}
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        {btn('Bulleted list', <FormatListBulletedIcon fontSize="small" />, () => exec('insertUnorderedList'))}
        {btn('Numbered list', <FormatListNumberedIcon fontSize="small" />, () => exec('insertOrderedList'))}
        {btn('Add or edit link', <LinkIcon fontSize="small" />, addLink)}
        {btn('Remove link', <LinkOffIcon fontSize="small" />, removeLink)}
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        {btn('Clear formatting', <CodeIcon fontSize="small" />, () => exec('removeFormat'))}
        {variables.length > 0 && (
          <>
            <Box sx={{ flexGrow: 1 }} />
            <Button size="small"
              onMouseDown={e => { e.preventDefault(); rememberCaret(); }}
              onClick={e => setVarAnchor(e.currentTarget)}>
              Insert variable
            </Button>
            <Menu anchorEl={varAnchor} open={Boolean(varAnchor)} onClose={() => setVarAnchor(null)}>
              {variables.map(v => (
                <MenuItem key={v} onClick={() => { setVarAnchor(null); insertAtCaret(`[[${v}]]`); }}>
                  <code>{`[[${v}]]`}</code>
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </Box>
      <Box
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        onKeyUp={rememberCaret}
        onMouseUp={rememberCaret}
        // Paste as plain text: pasting from Docs or Word otherwise drags in
        // spans, classes and stylesheet references that break in email.
        onPaste={e => {
          e.preventDefault();
          const text = e.clipboardData.getData('text/plain');
          document.execCommand('insertText', false, text);
          emit();
        }}
        sx={{
          minHeight, p: 2, outline: 'none', fontFamily: 'Arial, sans-serif',
          fontSize: '0.95rem', lineHeight: 1.6, color: '#333',
          '& a': { color: '#2E6DA4' },
          '& h3': { color: '#1E3A5F', marginBottom: '8px' },
          '& p': { margin: '0 0 12px' },
        }}
      />
    </Box>
  );
}
